import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Play } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  playOnLudolio,
  toast,
} from '@ludolio/shared-ui';
import { EmptyState, ReleaseStatusBadge } from '@/components/common';
import { BuildsPanel } from '@/components/project/BuildsPanel';
import { HistoryPanel } from '@/components/project/HistoryPanel';
import { MembersPanel } from '@/components/project/MembersPanel';
import { PublishedPanel } from '@/components/project/PublishedPanel';
import { ShowcasePanel } from '@/components/project/ShowcasePanel';
import { LineagePanel } from '@/components/project/LineagePanel';
import { QuizzesPanel } from '@/components/project/QuizzesPanel';
import { ReleaseDetailsForm } from '@/components/project/ReleaseDetailsForm';
import { RemixSettings } from '@/components/project/RemixSettings';
import { ReleaseImages } from '@/components/project/ReleaseImages';
import { SettingsPanel } from '@/components/project/SettingsPanel';
import { SourcePackagePanel } from '@/components/project/SourcePackagePanel';
import { getErrorMessage, labsApi } from '@/lib/api';
import { useLabs } from '@/lib/labs-context';
import type { LabProjectDetail } from '@/lib/types';

const POLL_INTERVAL_MS = 5000;
const ACTIVE_BUILD_STATUSES = new Set(['queued', 'scanning']);

export const Route = createFileRoute('/$orgSlug/projects/$projectId')({
  component: ProjectPage,
});

function ProjectPage() {
  const { projectId } = Route.useParams();
  const labs = useLabs();
  const [tab, setTab] = useState(() => (window.location.hash === '#lineage' ? 'lineage' : 'release'));
  const navigate = useNavigate();
  const [detail, setDetail] = useState<LabProjectDetail | null>(null);
  const [error, setError] = useState('');
  const [acting, setActing] = useState(false);

  const backToProjects = useCallback(
    () => navigate({ to: '/$orgSlug/projects', params: { orgSlug: labs.organization.slug } }),
    [labs.organization.slug, navigate],
  );

  const load = useCallback(async () => {
    try {
      setDetail(await labsApi.getProject(projectId));
      setError('');
    } catch (err) {
      setError(getErrorMessage(err, 'This project could not be loaded.'));
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const hasActiveScan = Boolean(
    detail?.openRelease?.builds.some((build) => ACTIVE_BUILD_STATUSES.has(build.status))
    || (detail?.openRelease?.sourcePackage
      && ACTIVE_BUILD_STATUSES.has(detail.openRelease.sourcePackage.status)),
  );
  useEffect(() => {
    if (!hasActiveScan) return;
    const timer = window.setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [hasActiveScan, load]);

  const act = async (action: () => Promise<LabProjectDetail>, success: string) => {
    setActing(true);
    try {
      setDetail(await action());
      toast.success(success);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setActing(false);
    }
  };

  if (error && !detail) return <EmptyState title={error} action={<Button variant="outline" onClick={backToProjects}>Back to projects</Button>} />;
  if (!detail) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const { openRelease, publishedRelease, permissions } = detail;
  const release = openRelease ?? publishedRelease;
  const showcaseRefreshKey = [
    release?.id ?? '',
    release?.status ?? '',
    release?.sourcePackage?.status ?? '',
  ].join(':');
  const hasPublishedVersion = Boolean(publishedRelease) || detail.history.some((entry) =>
    entry.status === 'published' || entry.status === 'superseded' || entry.status === 'withdrawn');
  const editable = openRelease?.status === 'draft' || openRelease?.status === 'changes_requested';
  const hasUnit = Boolean(detail.project.unit);
  const title = release?.title ?? detail.project.title;
  const takenDown = publishedRelease || openRelease ? undefined : detail.history.find((entry) => entry.status === 'withdrawn');
  const isTakenDown = Boolean(takenDown);
  const canPlay = Boolean(detail.project.appId)
    && Boolean(release?.builds.some((build) => build.status === 'passed'));

  const primaryAction = (() => {
    if (editable && openRelease) {
      return (
        <Button disabled={acting} onClick={() => void act(
          () => labsApi.submitRelease(openRelease.id),
          permissions.canPublishDirectly ? 'Published' : 'Submitted for review',
        )}>
          {permissions.canPublishDirectly ? 'Publish' : 'Submit for review'}
        </Button>
      );
    }
    if (openRelease?.status === 'in_review') {
      return (
        <>
          <Button variant="outline" disabled={acting} onClick={() => void act(() => labsApi.cancelSubmission(openRelease.id), 'Submission cancelled')}>
            Cancel submission
          </Button>
          {permissions.canPublishDirectly && (
            <Button
              disabled={acting}
              onClick={() => void act(async () => {
                await labsApi.approveRelease(openRelease.id);
                return labsApi.getProject(detail.project.id);
              }, 'Published')}
            >
              Publish
            </Button>
          )}
        </>
      );
    }
    if (permissions.canStartRelease) {
      return (
        <Button disabled={acting} onClick={() => void act(() => labsApi.startRelease(detail.project.id), 'New version started')}>
          Start new version
        </Button>
      );
    }
    return null;
  })();

  return (
    <div className="space-y-6">
      <Link
        to="/$orgSlug/projects"
        params={{ orgSlug: labs.organization.slug }}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Projects
      </Link>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <h1 className="truncate text-2xl font-semibold tracking-tight">{title}</h1>
          {isTakenDown && <ReleaseStatusBadge status="withdrawn" />}
          {release && <ReleaseStatusBadge status={release.status} />}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canPlay && (
            <Button variant="outline" onClick={() => playOnLudolio(detail.project.appId!)}>
              <Play />
              Open in Ludolio
            </Button>
          )}
          {primaryAction}
        </div>
      </div>

      {openRelease?.status === 'changes_requested' && openRelease.reviewNote && (
        <Alert>
          <AlertTitle>Changes requested</AlertTitle>
          <AlertDescription className="whitespace-pre-line">{openRelease.reviewNote}</AlertDescription>
        </Alert>
      )}
      {openRelease?.status === 'in_review' && (
        <Alert>
          <AlertTitle>Waiting for review</AlertTitle>
          <AlertDescription>Reviewers can play this version in Ludolio. Cancel the submission to make changes.</AlertDescription>
        </Alert>
      )}
      {isTakenDown && (
        <Alert>
          <AlertTitle>Taken down</AlertTitle>
          <AlertDescription className="whitespace-pre-line">
            {takenDown?.reviewNote ? `${takenDown.reviewNote}\n\n` : ''}Start a new version to submit again.
          </AlertDescription>
        </Alert>
      )}

      {publishedRelease && <PublishedPanel release={publishedRelease} hasUnit={hasUnit} onChange={setDetail} />}
      {hasPublishedVersion && <ShowcasePanel projectId={detail.project.id} refreshKey={showcaseRefreshKey} />}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="release">{openRelease ? `Version ${openRelease.version}` : 'Release'}</TabsTrigger>
          <TabsTrigger value="lineage">Lineage</TabsTrigger>
          <TabsTrigger value="quizzes">Quizzes</TabsTrigger>
          <TabsTrigger value="team">Team</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="release" className="mt-6 space-y-10">
          {release ? (
            <>
              <ReleaseDetailsForm release={release} hasUnit={hasUnit} editable={editable} onChange={setDetail} />
              <ReleaseImages release={release} editable={editable} onChange={setDetail} />
              <BuildsPanel release={release} reusableBuilds={detail.reusableBuilds} editable={editable} onChange={setDetail} />
              <SourcePackagePanel
                release={release}
                reusableSourcePackages={detail.reusableSourcePackages}
                editable={editable}
                onChange={setDetail}
              />
              <RemixSettings release={release} editable={editable} onChange={setDetail} />
            </>
          ) : (
            <EmptyState title="There is no version in progress" action={primaryAction} />
          )}
        </TabsContent>

        <TabsContent value="lineage" className="mt-6">
          <LineagePanel projectId={detail.project.id} />
        </TabsContent>

        <TabsContent value="quizzes" className="mt-6">
          <QuizzesPanel projectId={detail.project.id} />
        </TabsContent>

        <TabsContent value="team" className="mt-6">
          <MembersPanel detail={detail} />
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <HistoryPanel history={detail.history} />
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <SettingsPanel detail={detail} onChange={setDetail} onDeleted={backToProjects} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
